import { useEffect, useRef, useState } from "react";

// On phones the site pages one screen at a time, and each page's calligraphy
// inks in again every time it is swiped onto (matches the .m-page breakpoint).
const REPLAY_QUERY = "(max-width: 767px)";

/**
 * Whether an ink reveal should run: on mount (after `play`), or once the
 * element is half in view. On phones it resets when the element leaves the
 * screen, so it replays on every visit. Shared by InkedImage and InkedText.
 */
export const useInkReveal = (onMount, play) => {
	const ref = useRef(null);
	const [inked, setInked] = useState(false);

	useEffect(() => {
		if (!play) return undefined;
		const el = ref.current;
		const replay = window.matchMedia(REPLAY_QUERY).matches;
		let frame = 0;
		if (onMount) {
			// Two frames so the hidden state is painted before the transition starts.
			frame = requestAnimationFrame(() => {
				frame = requestAnimationFrame(() => setInked(true));
			});
		}
		const stop = () => cancelAnimationFrame(frame);
		if (!el || !("IntersectionObserver" in window)) {
			if (!onMount) setInked(true);
			return stop;
		}
		if (onMount && !replay) return stop;
		const io = new IntersectionObserver(
			([entry]) => {
				if (entry.intersectionRatio >= 0.5) {
					setInked(true);
					if (!replay) io.disconnect();
				} else if (replay && !entry.isIntersecting) {
					// Fully off screen: hide again (instantly, the transition only
					// runs while .is-inked is on) so the next visit inks it anew.
					setInked(false);
				}
			},
			{ threshold: [0, 0.5] }
		);
		io.observe(el);
		return () => {
			stop();
			io.disconnect();
		};
	}, [onMount, play]);

	return [ref, inked];
};

/**
 * Pre-rendered gold calligraphy revealed right to left, the
 * direction the pen travels. The reveal is a CSS mask slide, so the browser
 * only repaints a flat image instead of re-running paint filters.
 */
const InkedImage = ({
	src,
	srcSet,
	sizes,
	width,
	height,
	alt = "",
	className = "",
	delay = 0,
	duration = 2.2,
	onMount = false,
	play = true, // false holds the reveal, e.g. until the loader is gone
	priority = false,
}) => {
	const [ref, inked] = useInkReveal(onMount, play);

	return (
		<img
			ref={ref}
			src={src}
			srcSet={srcSet}
			sizes={sizes}
			width={width}
			height={height}
			alt={alt}
			aria-hidden={alt ? undefined : true}
			decoding="async"
			loading={priority ? "eager" : "lazy"}
			fetchpriority={priority ? "high" : undefined}
			className={`ink-reveal ${inked ? "is-inked" : ""} ${className}`}
			style={{ "--ink-delay": `${delay}s`, "--ink-duration": `${duration}s` }}
		/>
	);
};

export default InkedImage;
